import { useState, useEffect } from "react";
import { X, Link as LinkIcon, Search } from "lucide-react";
import { Equipment } from "../../types/equipment";
import { EquipmentType } from "../../types/equipment-type";
import { Store } from "../../types/store";
import { getEquipment } from "../../services/equipmentApi";
import { getEquipmentTypes } from "../../services/equipmentTypeApi";
import { getStores } from "../../services/storeApi";
import { getAllocatedEquipmentByStore, getEquipmentByOrderNumber } from "../../services/equipmentApi";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { getTechnicianByEmail } from "../../services/technicianApi";

interface SelectedEquipment {
    id: number;
    quantity: number;
    allocationType: 'store' | 'equipment';
    storeId?: number;
    allocatedId?: number;
    allocationDate: string;
    allocationNotes?: string;
    store_number?: string;
    groupedIds?: number[];
    isGrouped: boolean;
}

interface AddTaskEquipmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (selectedEquipment: { 
        id: number; 
        quantity: number; 
        order_number?: string;
        name?: string;
        serial_number?: string;
        equipment_type?: {
            name: string;
            id?: number;
        };
        equipment_subtype_id?: number;
        groupedIds?: number[];
    }[]) => void;
    existingEquipmentIds: number[];
    jobcardStoreNumber?: string;
}

export default function AddTaskEquipmentModal({
    isOpen,
    onClose,
    onAdd,
    existingEquipmentIds,
    jobcardStoreNumber,
}: AddTaskEquipmentModalProps) {
    const { user } = useAuth();
    const [equipment, setEquipment] = useState<Equipment[]>([]);
    const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedEquipmentType, setSelectedEquipmentType] = useState<number | null>(null);
    const [selectedEquipmentSubtype, setSelectedEquipmentSubtype] = useState<number | null>(null);
    const [selectedEquipment, setSelectedEquipment] = useState<Record<number, SelectedEquipment>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredEquipment, setFilteredEquipment] = useState<(Equipment & { stockQuantity: number; isGrouped: boolean; groupedIds?: number[] })[]>([]);
    const [allocatedEquipment, setAllocatedEquipment] = useState<Equipment[]>([]);
    const [orderNumber, setOrderNumber] = useState("");
    const [isLoadingOrder, setIsLoadingOrder] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const [typesResponse, storesResponse] = await Promise.all([
                    getEquipmentTypes(),
                    getStores(),
                ]);

                setEquipmentTypes(typesResponse.data.data);
                setStores(storesResponse.data.data);

                // Pre-select store if jobcardStoreNumber is provided
                console.log("jobcardStoreNumber", jobcardStoreNumber);
                if (jobcardStoreNumber) {
                    const matchingStore = storesResponse.data.data.find(
                        (store) => store.storenumber === jobcardStoreNumber
                    );

                    if (matchingStore) {
                        // Pre-select store for all unallocated equipment with store allocation type
                        const preSelectedEquipment: Record<number, SelectedEquipment> = {};
                        setSelectedEquipment(preSelectedEquipment);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch data:", error);
                toast.error("Failed to fetch equipment data");
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchData();
        }
    }, [user?.id, isOpen, existingEquipmentIds, jobcardStoreNumber]);

    useEffect(() => {
        const fetchEquipmentByType = async () => {
            if (!selectedEquipmentType) return;

            try {
                setIsLoading(true);
                const response = await getEquipment({
                    type: selectedEquipmentType.toString(),
                    is_subtype: false,
                    unassigned: true,
                    per_page: -1 // Request all results without pagination
                });
                const equipmentData = response.data.data.data || response.data.data || [];
                setEquipment(equipmentData);
            } catch (error) {
                console.error("Failed to fetch equipment by type:", error);
                toast.error("Failed to fetch equipment");
            } finally {
                setIsLoading(false);
            }
        };

        fetchEquipmentByType();
    }, [selectedEquipmentType, existingEquipmentIds]);

    // Update the useEffect for filtering to include grouping and account for selected quantities
    useEffect(() => {
        const filtered = equipment.filter(
            (eq) =>
                eq.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                eq.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                eq.model_number?.toLowerCase().includes(searchQuery.toLowerCase())
        );

        // First group the equipment
        const grouped = filtered.reduce((acc, eq) => {
            // If item has serial number, keep it individual
            if (eq.serial_number) {
                acc.push({
                    ...eq,
                    stockQuantity: 1,
                    isGrouped: false
                });
                return acc;
            }

            // Find existing group
            const existingGroup = acc.find(item => 
                item.name === eq.name && 
                item.equipment_type_id === eq.equipment_type_id &&
                item.equipment_subtype_id === eq.equipment_subtype_id &&
                !item.serial_number
            );

            if (existingGroup && existingGroup.isGrouped) {
                existingGroup.stockQuantity += 1;
                existingGroup.groupedIds = [...(existingGroup.groupedIds || []), eq.id];
            } else {
                acc.push({
                    ...eq,
                    stockQuantity: 1,
                    isGrouped: true,
                    groupedIds: [eq.id]
                });
            }
            return acc;
        }, [] as (Equipment & { stockQuantity: number; isGrouped: boolean; groupedIds?: number[] })[]);

        // Now prepare the equipment to display
        let availableEquipment = grouped.map(eq => {
            // Find the corresponding selected item
            const selectedItem = Object.values(selectedEquipment).find(item => item.id === eq.id);
            
            // If this item is selected, update its display properties
            if (selectedItem) {
                return {
                    ...eq,
                    stockQuantity: eq.stockQuantity // Keep the original stock quantity
                };
            }
            
            return eq;
        });

        setFilteredEquipment(availableEquipment);
    }, [equipment, searchQuery, selectedEquipment, orderNumber]);

    useEffect(() => {
        const fetchAllocatedEquipment = async () => {
            if (!jobcardStoreNumber) return;
            const response = await getAllocatedEquipmentByStore(jobcardStoreNumber);
            setAllocatedEquipment(response.data.data);
        };
        fetchAllocatedEquipment();
    }, [jobcardStoreNumber]);

    const handleTypeChange = async (typeId: number) => {
        setSelectedEquipmentType(typeId);
        setSelectedEquipmentSubtype(null);

        if (!typeId || !user?.email) {
            setEquipment([]);
            return;
        }

        try {
            setIsLoading(true);
            // Get technician ID first
            const techResponse = await getTechnicianByEmail(user.email);
            if (!techResponse.data.data) {
                setEquipment([]);
                return;
            }

            const response = await getEquipment({
                status: "active",
                allocated: false,
                assigned_to: techResponse.data.data.id,
                type: typeId.toString(),
                is_subtype: false,
                unassigned: true,
                per_page: -1 // Request all results without pagination
            });

            // Handle response data
            const equipmentData = response.data.data.data || response.data.data || [];
            // Additional frontend filtering to ensure we don't show allocated equipment
            const filteredEquipment = equipmentData.filter(
                (eq: Equipment) =>
                    !existingEquipmentIds.includes(eq.id) && // Not in task_equipment
                    !eq.allocated_id && // Not allocated to equipment
                    !eq.store_number // Not allocated to store
            );
            setEquipment(filteredEquipment);
        } catch (error) {
            console.error("Failed to fetch equipment:", error);
            toast.error("Failed to fetch equipment");
            setEquipment([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubtypeChange = async (subtypeId: number) => {
        setSelectedEquipmentSubtype(subtypeId === selectedEquipmentSubtype ? null : subtypeId);
        
        try {
            setIsLoading(true);
            
            // If no subtype selected or clicking the currently selected subtype, show all equipment for the selected type
            if (!subtypeId || subtypeId === selectedEquipmentSubtype) {
                if (selectedEquipmentType) {
                    const response = await getEquipment({
                        type: selectedEquipmentType.toString(),
                        is_subtype: false,
                        unassigned: true,
                        allocated: false,
                        per_page: -1 // Request all results without pagination
                    });
                    const equipmentData = response.data.data.data || response.data.data || [];
                    // Additional frontend filtering
                    const filteredEquipment = equipmentData.filter(
                        (eq: Equipment) =>
                            !existingEquipmentIds.includes(eq.id) && // Not in task_equipment
                            !eq.allocated_id && // Not allocated to equipment
                            !eq.store_number // Not allocated to store
                    );
                    setEquipment(filteredEquipment);
                }
            } else {
                // Use the new subtype endpoint for specific subtype
                const response = await getEquipment({
                    type: subtypeId.toString(),
                    is_subtype: true,
                    unassigned: true,
                    allocated: false,
                    per_page: -1 // Request all results without pagination
                });
                const equipmentData = response.data.data.data || response.data.data || [];
                // Additional frontend filtering
                const filteredEquipment = equipmentData.filter(
                    (eq: Equipment) =>
                        !existingEquipmentIds.includes(eq.id) && // Not in task_equipment
                        !eq.allocated_id && // Not allocated to equipment
                        !eq.store_number // Not allocated to store
                );
                setEquipment(filteredEquipment);
            }
        } catch (error) {
            console.error('Error fetching equipment by subtype:', error);
            toast.error('Failed to load equipment');
        } finally {
            setIsLoading(false);
        }
    };

    const validateQuantity = (value: number | null | undefined, maxQuantity: number) => {
        const numValue = Number(value);
        if (isNaN(numValue) || value === null || value === undefined) return 1;
        if (numValue < 1) return 1;
        if (numValue > maxQuantity) return maxQuantity;
        return Math.floor(numValue); // Ensure whole numbers
    };

    const handleQuantityChange = (equipmentId: number, newValue: number, maxQuantity: number) => {
        const validatedQuantity = validateQuantity(newValue, maxQuantity);
        
        setSelectedEquipment(prev => {
            const currentSelection = prev[equipmentId];
            const equipment = filteredEquipment.find(e => e.id === equipmentId);

            if (!equipment) return prev;

            // If we're setting quantity to 0, remove the selection
            if (validatedQuantity === 0) {
                const { [equipmentId]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [equipmentId]: {
                    ...currentSelection,
                    id: equipmentId,
                    quantity: validatedQuantity,
                    isGrouped: equipment.isGrouped,
                    groupedIds: equipment.isGrouped ? 
                        equipment.groupedIds : // Don't slice the array - use all IDs
                        undefined,
                    allocationType: currentSelection?.allocationType || 'store',
                    allocationDate: currentSelection?.allocationDate || new Date().toISOString().split('T')[0]
                }
            };
        });
    };

    const updateSelectedEquipment = (
        equipmentId: number,
        updates: Partial<Omit<SelectedEquipment, 'id' | 'quantity' | 'isGrouped'>>
    ) => {
        const equipment = filteredEquipment.find(eq => eq.id === equipmentId);
        if (!equipment) return;

        setSelectedEquipment(prev => {
            // If removing selection
            if (prev[equipmentId] && !updates.allocationType) {
                const { [equipmentId]: _, ...rest } = prev;
                return rest;
            }

            // Get the current selection if it exists
            const currentSelection = prev[equipmentId];

            // If adding or updating selection
            const baseSelection: SelectedEquipment = {
                id: equipmentId,
                quantity: currentSelection?.quantity || 1, // Preserve existing quantity or default to 1
                isGrouped: equipment.isGrouped,
                allocationType: 'store',
                allocationDate: new Date().toISOString().split('T')[0],
                groupedIds: equipment.groupedIds,
                ...updates
            };

            // Force store allocation if jobcardStoreNumber is provided
            if (!prev[equipmentId] && jobcardStoreNumber) {
                const matchingStore = stores.find(store => store.storenumber === jobcardStoreNumber);
                if (matchingStore) {
                    baseSelection.allocationType = 'store';
                    baseSelection.storeId = matchingStore.id;
                    baseSelection.store_number = jobcardStoreNumber;
                }
            }

            return {
                ...prev,
                [equipmentId]: baseSelection
            };
        });
    };

    // Helper function to get default store id from jobcardStoreNumber
    // const getDefaultStoreId = () => {
    //     if (jobcardStoreNumber) {
    //         const matchingStore = stores.find(store => store.storenumber === jobcardStoreNumber);
    //         return matchingStore?.id;
    //     }
    //     return undefined;
    // };

    const handleAddEquipment = () => {
        console.log("Selected equipment before processing:", selectedEquipment);
        const processedItems = Object.values(selectedEquipment).flatMap(item => {
            if (!item.isGrouped) {
                // Find the equipment details to include order number
                const equipmentItem = equipment.find(eq => eq.id === item.id);
                if (!equipmentItem) return [];

                console.log(`Processing non-grouped item: ${equipmentItem.name} (ID: ${item.id})`);
                return [{
                    id: item.id,
                    quantity: item.quantity || 1,
                    order_number: equipmentItem.order_number,
                    name: equipmentItem.name,
                    serial_number: equipmentItem.serial_number,
                    equipment_type: equipmentItem.equipment_type ? {
                        name: equipmentItem.equipment_type.name,
                        id: equipmentItem.equipment_type.id
                    } : undefined,
                    equipment_subtype_id: equipmentItem.equipment_subtype_id ? 
                        Number(equipmentItem.equipment_subtype_id) : 
                        undefined
                }];
            }

            const groupedEquipmentItem = filteredEquipment.find(eq => eq.id === item.id);
            if (!groupedEquipmentItem?.groupedIds || groupedEquipmentItem.groupedIds.length === 0) {
                console.log(`Warning: Grouped item ${item.id} has no groupedIds`);
                return [];
            }

            console.log(`Processing grouped item: ID ${item.id} with groupedIds: ${groupedEquipmentItem.groupedIds.join(', ')}`);
            
            // For grouped items, return the main item with the full quantity and ensure groupedIds are included
            return [{
                id: item.id,
                quantity: item.quantity || 1,
                groupedIds: groupedEquipmentItem.groupedIds,
                order_number: groupedEquipmentItem.order_number,
                name: groupedEquipmentItem.name,
                serial_number: groupedEquipmentItem.serial_number || "",
                equipment_type: groupedEquipmentItem.equipment_type ? {
                    name: groupedEquipmentItem.equipment_type.name,
                    id: groupedEquipmentItem.equipment_type.id
                } : undefined,
                equipment_subtype_id: groupedEquipmentItem.equipment_subtype_id ? 
                    Number(groupedEquipmentItem.equipment_subtype_id) : 
                    undefined
            }];
        });

        console.log("Final processed equipment items:", processedItems);
        onAdd(processedItems);
        onClose();
    };

    // New function to fetch equipment by order number
    const handleFilterByOrder = async () => {
        if (!orderNumber.trim()) {
            toast.error("Please enter an order number");
            return;
        }

        try {
            setIsLoadingOrder(true);
            setSelectedEquipmentType(null);
            setSelectedEquipmentSubtype(null);
            
            // Use the equipmentApi service function
            const response = await getEquipmentByOrderNumber(orderNumber.trim());
            
            const orderEquipment = response.data.data || [];
            
            if (orderEquipment.length === 0) {
                toast.error(`No equipment found with order #${orderNumber}`);
                return;
            }
            
            // Group the equipment for determining quantities
            const grouped = orderEquipment.reduce((acc: (Equipment & { stockQuantity: number; isGrouped: boolean; groupedIds?: number[] })[], eq: Equipment) => {
                // If item has serial number, keep it individual
                if (eq.serial_number) {
                    acc.push({
                        ...eq,
                        stockQuantity: 1,
                        isGrouped: false
                    });
                    return acc;
                }

                // Find existing group
                const existingGroup = acc.find((item: Equipment & { stockQuantity: number; isGrouped: boolean; groupedIds?: number[] }) => 
                    item.name === eq.name && 
                    item.equipment_type_id === eq.equipment_type_id &&
                    item.equipment_subtype_id === eq.equipment_subtype_id &&
                    !item.serial_number
                );

                if (existingGroup && existingGroup.isGrouped) {
                    existingGroup.stockQuantity += 1;
                    existingGroup.groupedIds = [...(existingGroup.groupedIds || []), eq.id];
                } else {
                    acc.push({
                        ...eq,
                        stockQuantity: 1,
                        isGrouped: true,
                        groupedIds: [eq.id]
                    });
                }
                return acc;
            }, [] as (Equipment & { stockQuantity: number; isGrouped: boolean; groupedIds?: number[] })[]);
            
            // Set both equipment and filteredEquipment states
            setEquipment(orderEquipment);
            setFilteredEquipment(grouped);
            
            // Auto-select all equipment from this order with appropriate quantities
            const newSelections: Record<number, SelectedEquipment> = {};
            grouped.forEach((eq: Equipment & { stockQuantity: number; isGrouped: boolean; groupedIds?: number[] }) => {
                const matchingStore = stores.find(store => store.storenumber === jobcardStoreNumber);
                
                // For grouped items, use the stockQuantity as the quantity
                // For non-grouped items (with serial numbers), use 1
                const quantity = eq.isGrouped ? eq.stockQuantity : 1;
                
                newSelections[eq.id] = {
                    id: eq.id,
                    quantity: quantity,
                    isGrouped: eq.isGrouped,
                    allocationType: 'store',
                    allocationDate: new Date().toISOString().split('T')[0],
                    storeId: matchingStore?.id,
                    store_number: jobcardStoreNumber,
                    groupedIds: eq.groupedIds
                };
            });
            
            // Set all items as selected
            setSelectedEquipment(newSelections);
            
            toast.success(`Found ${orderEquipment.length} equipment items from order #${orderNumber}`);
        } catch (error) {
            console.error("Failed to fetch equipment by order:", error);
            toast.error("Failed to fetch equipment for this order number");
        } finally {
            setIsLoadingOrder(false);
        }
    };

    if (!isOpen) return null;

    const selectedType = equipmentTypes.find(
        (type) => type.id === selectedEquipmentType
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200"
                >
                    <X size={24} />
                </button>

                <div className="p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                        Add Equipment to Task
                    </h2>

                    {/* New Order Number Filter Section */}
                    <div className="mb-6 p-4 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                        <h3 className="text-md font-medium mb-2 text-blue-700 dark:text-blue-300">
                            Quick Filter by Order Number
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                            <input
                                type="text"
                                value={orderNumber}
                                onChange={(e) => setOrderNumber(e.target.value)}
                                placeholder="Enter order number"
                                className="flex-1 px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <button
                                onClick={handleFilterByOrder}
                                disabled={isLoadingOrder}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 sm:min-w-[80px]"
                            >
                                {isLoadingOrder ? "Loading..." : "Load"}
                            </button>
                        </div>
                        <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                            Enter an order number to automatically fetch and select all equipment from that order.
                        </p>
                    </div>

                    <div className="space-y-4 mb-6">
                        {/* Equipment Type Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Equipment Type
                            </label>
                            <select
                                value={selectedEquipmentType || ""}
                                onChange={(e) => handleTypeChange(Number(e.target.value))}
                                className="mt-1 block w-full rounded-md border border-gray-700 px-2 dark:border-gray-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="">Select Equipment Type</option>
                                {equipmentTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Equipment Subtype Selection */}
                        {selectedType?.subtypes && selectedType.subtypes.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Equipment Subtype
                                </label>
                                <select
                                    value={selectedEquipmentSubtype || ""}
                                    onChange={(e) => handleSubtypeChange(Number(e.target.value))}
                                    className="mt-1 block w-full rounded-md border border-gray-700 px-2 dark:border-gray-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">All Subtypes</option>
                                    {selectedType.subtypes.map((subtype) => (
                                        <option key={subtype.id} value={subtype.id}>
                                            {subtype.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="text-center py-4 text-gray-600 dark:text-gray-400">
                            Loading...
                        </div>
                    ) : equipment.length === 0 ? (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                            {selectedEquipmentType
                                ? "No available equipment found for selected type"
                                : "Please select an equipment type"}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {orderNumber.trim() && Object.keys(selectedEquipment).length > 0 && (
                                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                    <p className="text-sm text-green-700 dark:text-green-300">
                                        <span className="font-medium">✓ All equipment from order #{orderNumber} has been selected.</span> You can now click "Add Selected Equipment" to add all {Object.keys(selectedEquipment).length} items to your task.
                                    </p>
                                </div>
                            )}
                            
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search equipment by name, serial number, or model..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 sm:text-sm transition-colors duration-200"
                                />
                            </div>

                            {filteredEquipment.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                    No equipment found matching your search
                                </div>
                            ) : (
                                filteredEquipment.map((eq: Equipment & { stockQuantity: number; isGrouped: boolean; groupedIds?: number[] }) => (
                                    <div
                                        key={eq.id}
                                        className={`border dark:border-gray-700 rounded p-4 ${
                                            selectedEquipment[eq.id]
                                                ? "bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700"
                                                : "hover:bg-gray-50 dark:hover:bg-gray-700"
                                        } transition-colors duration-200`}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <h3 className="font-medium text-gray-900 dark:text-white">
                                                    {eq.name}
                                                </h3>
                                                {eq.isGrouped && eq.groupedIds && (
                                                    <p className="text-xs bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 p-1 rounded mb-1 break-all">
                                                        Group IDs: {eq.groupedIds.join(', ')}
                                                    </p>
                                                )}
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {eq.serial_number ? (
                                                        `${eq.serial_number} | ${eq.model_number} | 1 in stock${eq.order_number ? ` | Order #${eq.order_number}` : ''}`
                                                    ) : (
                                                        `${eq.model_number || 'No model'} | ${eq.stockQuantity} in stock${eq.order_number ? ` | Order #${eq.order_number}` : ''}`
                                                    )}
                                                </p>
                                                {eq.user && (
                                                    <p className="text-sm mt-1">
                                                        <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-full text-xs">
                                                            Assigned to: {eq.user.name}
                                                        </span>
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                {eq.isGrouped && selectedEquipment[eq.id] && (
                                                    <div className="flex items-center space-x-2">
                                                        <label className="text-sm text-gray-600 dark:text-gray-400">Qty:</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={eq.stockQuantity}
                                                            value={selectedEquipment[eq.id]?.quantity || 1}
                                                            onChange={(e) => handleQuantityChange(eq.id, parseInt(e.target.value), eq.stockQuantity)}
                                                            className="w-20 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                        />
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        if (selectedEquipment[eq.id]) {
                                                            const { [eq.id]: _, ...rest } = selectedEquipment;
                                                            setSelectedEquipment(rest);
                                                        } else {
                                                            updateSelectedEquipment(eq.id, {
                                                                allocationType: "store",
                                                                allocationDate: new Date().toISOString().split("T")[0],
                                                            });
                                                        }
                                                    }}
                                                    className={`px-3 py-1 rounded text-sm flex items-center transition-colors duration-200 ${
                                                        selectedEquipment[eq.id]
                                                            ? "bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30"
                                                            : "bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30"
                                                    }`}
                                                >
                                                    <LinkIcon size={16} className="mr-1" />
                                                    {selectedEquipment[eq.id] ? "Remove" : "Allocate"}
                                                </button>
                                            </div>
                                        </div>

                                        {selectedEquipment[eq.id] && (
                                            <div className="mt-4 space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Allocation Type
                                                    </label>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                updateSelectedEquipment(eq.id, {
                                                                    allocationType: "store",
                                                                    allocatedId: undefined,
                                                                    storeId: jobcardStoreNumber
                                                                        ? stores.find(
                                                                            (s) =>
                                                                                s.storenumber === jobcardStoreNumber
                                                                        )?.id
                                                                        : undefined,
                                                                    store_number: jobcardStoreNumber,
                                                                })
                                                            }
                                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                                                selectedEquipment[eq.id]?.allocationType ===
                                                                "store"
                                                                    ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-500 dark:border-indigo-400"
                                                                    : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                            }`}
                                                        >
                                                            Store Allocation
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                updateSelectedEquipment(eq.id, {
                                                                    allocationType: "equipment",
                                                                    storeId: undefined,
                                                                    store_number: undefined,
                                                                })
                                                            }
                                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                                                selectedEquipment[eq.id]?.allocationType ===
                                                                "equipment"
                                                                    ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-500 dark:border-indigo-400"
                                                                    : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                            }`}
                                                        >
                                                            Equipment Allocation
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Store Selection - Show when store allocation is selected */}
                                                {selectedEquipment[eq.id]?.allocationType ===
                                                    "store" && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Select Store
                                                        </label>
                                                        <select
                                                            value={selectedEquipment[eq.id]?.storeId || ""}
                                                            onChange={(e) => {
                                                                const selectedStore = stores.find(
                                                                    (store) =>
                                                                        store.id === parseInt(e.target.value)
                                                                );
                                                                updateSelectedEquipment(eq.id, {
                                                                    storeId: selectedStore
                                                                        ? selectedStore.id
                                                                        : undefined,
                                                                    store_number: selectedStore
                                                                        ? selectedStore.storenumber
                                                                        : undefined,
                                                                    allocationType: "store",
                                                                });
                                                            }}
                                                            className="mt-1 block w-full rounded-md border border-gray-700 px-2 dark:border-gray-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                        >
                                                            <option value="">Select a store</option>
                                                            {stores.map((store) => (
                                                                <option key={store.id} value={store.id}>
                                                                    {store.name} ({store.storenumber})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                {/* Equipment Selection - Show when equipment allocation is selected */}
                                                {selectedEquipment[eq.id]?.allocationType ===
                                                    "equipment" && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Select Equipment to Allocate To
                                                        </label>
                                                        <select
                                                            value={
                                                                selectedEquipment[eq.id]?.allocatedId || ""
                                                            }
                                                            onChange={(e) =>
                                                                updateSelectedEquipment(eq.id, {
                                                                    allocatedId: e.target.value
                                                                        ? parseInt(e.target.value)
                                                                        : undefined,
                                                                    allocationType: "equipment",
                                                                })
                                                            }
                                                            className="mt-1 block w-full rounded-md border border-gray-700 px-2 dark:border-gray-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                        >
                                                            <option value="">Select Equipment</option>
                                                            {/* Show all equipment allocated to the store, regardless of technician */}
                                                            {equipment
                                                                .concat(allocatedEquipment)
                                                                .filter(
                                                                    (item, index, self) =>
                                                                        // Remove duplicates
                                                                        index ===
                                                                            self.findIndex((t) => t.id === item.id) &&
                                                                        // Only show equipment allocated to the store
                                                                        item.store_number === jobcardStoreNumber
                                                                )
                                                                .map((item) => (
                                                                    <option key={item.id} value={item.id}>
                                                                        {item.name}{" "}
                                                                        {item.serial_number
                                                                            ? `(S/N: ${item.serial_number})`
                                                                            : ""}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                        {jobcardStoreNumber &&
                                                            equipment.filter(
                                                                (item) =>
                                                                    item.store_number === jobcardStoreNumber
                                                            ).length === 0 && (
                                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                                    No equipment allocated to Store #
                                                                    {jobcardStoreNumber}
                                                                </p>
                                                            )}
                                                    </div>
                                                )}

                                                {/* Allocation Date - Show for both allocation types */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Allocation Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={
                                                            selectedEquipment[eq.id]?.allocationDate ||
                                                            new Date().toISOString().split("T")[0]
                                                        }
                                                        onChange={(e) =>
                                                            updateSelectedEquipment(eq.id, {
                                                                allocationDate: e.target.value,
                                                            })
                                                        }
                                                        className="mt-1 block w-full rounded-md border border-gray-700 px-2 dark:border-gray-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </div>

                                                {/* Allocation Notes - Show for both allocation types */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Allocation Notes
                                                    </label>
                                                    <textarea
                                                        value={
                                                            selectedEquipment[eq.id]?.allocationNotes || ""
                                                        }
                                                        onChange={(e) =>
                                                            updateSelectedEquipment(eq.id, {
                                                                allocationNotes: e.target.value,
                                                            })
                                                        }
                                                        className="mt-1 block w-full rounded-md border border-gray-700 px-2 dark:border-gray-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                        rows={3}
                                                        placeholder="Add any notes about this allocation..."
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end gap-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddEquipment}
                            disabled={Object.keys(selectedEquipment).length === 0}
                            className="px-4 py-2 text-white bg-indigo-600 dark:bg-indigo-500 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 transition-colors duration-200"
                        >
                            Add Selected Equipment
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
